import { twMerge } from 'tailwind-merge';
import { CryptoLogo } from '../CryptoLogo';
import { MultiSelect, SelectOption } from '../MultiSelect/MultiSelect';

export type TokenName = { tokenName: string } | { name: string } | { symbol: string };

const defaultGetTickerFromValue = (value: TokenName) => {
  if ('symbol' in value) return value.symbol;
  if ('name' in value) return value.name;
  return value.tokenName;
};

interface IconSelectProps<T extends TokenName> {
  options: SelectOption<T>[];
  value?: T;
  disabled?: boolean;
  onChange: (value?: SelectOption<T>) => void;
  className?: string;
  defaultTicker?: string;
  getTickerFromValue?: (value: T) => string;
}

export function IconSelect<T extends TokenName>({
  options,
  value,
  disabled,
  onChange,
  className,
  getTickerFromValue = defaultGetTickerFromValue,
}: IconSelectProps<T>) {
  // Add icons to each option
  const multiSelectOptions = options.map((option) => {
    const ticker = getTickerFromValue(option.value);
    return {
      ...option,
      icon: ticker ? <CryptoLogo tokenName={ticker.toUpperCase()} size={16} /> : null,
    };
  });

  const handleChange = (newValue: T | undefined) => {
    if (!newValue) {
      onChange(undefined);
      return;
    }

    const selectedOption = options.find(
      (opt) => getTickerFromValue(opt.value) === getTickerFromValue(newValue)
    );

    onChange(selectedOption);
  };

  return (
    <MultiSelect
      multiselect={false}
      options={multiSelectOptions}
      value={value}
      onChange={handleChange}
      className={twMerge('w-auto', options.length === 1 ? 'pointer-events-none' : '', className)}
      showSearchInput={false}
      buttonClassName="border-borderInversePrimary !px-2 !py-1 !text-xs"
      disabled={disabled}
      hideChevron
      panelOffset={4}
    />
  );
}
